import { useMemo } from 'react';
import styled from 'styled-components';
import { Col, Row } from 'react-grid-system';
import { useErrorHandlerContext, useUserLanguage } from '@/customHooks';
import { LoadingSpinner } from '../atoms';
import { advancedNormalizeDate } from '@/helpers';
import { useGetChargeDetailQuery } from '@/graphql/generated';

type ChargePaymentsListProps = {
  chargeId: string | null;
};

type PaymentData = {
  id: string;
  paymentDate: string;
  status: string;
  amount: string;
  order?: string | null;
};

const StyledGridTable = styled.div`
  padding-top: ${({ theme }) => theme.spacing.lg};
  padding-bottom: ${({ theme }) => theme.spacing.lg};
  border-radius: 12px;
  display: grid;
  grid-template-columns: 1fr 1fr 1fr 1fr 1fr;
  row-gap: 10px;
`;

const StyledGridHeader = styled.div`
  display: contents;
  & > div {
    font-weight: bold;
    text-align: left;
    padding: 15px;
    border-top: 1px solid grey;
    border-bottom: 1px solid gray;
  }
`;

const StyledGridRow = styled.div`
  display: contents;
  
  & > div {
    padding: 15px;
    background-color: ${({ theme }) => theme.colors.background2};
    font-size: 14px;
    padding-top: 1rem;
    padding-bottom: 1rem;
    align-self: stretch;
    place-content: center;
    border-bottom: solid 1px lightgray;
  }

  &:nth-of-type(odd) > div {
    background-color: ${({ theme }) => theme.colors.light};
  }
`;

const textsData = {
  en: {
    title: 'Partial Payments',
    tableHeader: {
      id: 'ID',
      paymentDate: 'Payment Date',
      status: 'Status',
      amount: 'Amount',
      order: 'Order',
    },
    noData: 'No payments found',
    generateOrderButton: 'Generate Partial Payment Order',
  },
  es: {
    title: 'Pagos Fraccionados',
    tableHeader: {
      id: 'ID',
      paymentDate: 'Fecha de Pago',
      status: 'Estado',
      amount: 'Monto',
      order: 'Orden',
    },
    noData: 'No se encontraron pagos',
    generateOrderButton: 'Generar Orden de Pago Parcial',
  },
};

export const ChargePaymentsList = ({ chargeId }: ChargePaymentsListProps) => {
  const userLanguage = useUserLanguage();
  const texts = userLanguage === 'es' ? textsData.es : textsData.en;

  const { open: errorHandle } = useErrorHandlerContext();

  const { data, error, loading } = useGetChargeDetailQuery({ variables: {
    chargeId: chargeId || '',
  }})

  const paymentsData = useMemo(() => {
    if (!data?.rentCharge) return [];

    const payment = data.rentCharge;

    return [{
      id: payment.id,
      paymentDate: payment.executionDate,
      status: payment.rentChargeStatusOption,
      amount: payment.amountValue,
      order: '-',
    }];
  }, [data]);

  if (error) errorHandle(error);

  if (loading) {
    return (
      <Row justify="center">
        <Col xs="content">
          <LoadingSpinner text />
        </Col>
      </Row>
    );
  }

  if (!paymentsData || paymentsData.length === 0) {
    return (
      <Row>
        <Col>
          <p><b>{texts.noData}</b></p>
        </Col>
      </Row>
    );
  }

  return (
    <StyledGridTable>
      <StyledGridHeader>
        <div>{texts.tableHeader.id}</div>
        <div>{texts.tableHeader.paymentDate}</div>
        <div>{texts.tableHeader.status}</div>
        <div>{texts.tableHeader.amount}</div>
        <div>{texts.tableHeader.order}</div>
      </StyledGridHeader>
      {paymentsData.map((payment: PaymentData) => (
        <StyledGridRow key={payment.id}>
          <div>{payment.id}</div>
          <div>{payment.paymentDate ? advancedNormalizeDate(userLanguage, payment.paymentDate) : '-'}</div>
          <div>{payment.status || '-'}</div>
          <div>{payment.amount || '-'}</div>
          <div>{payment.order || '-'}</div>
        </StyledGridRow>
      ))}
    </StyledGridTable>
  );
};

